import React, { useState, useEffect, useRef } from "react";
import { X, Network, Settings2, Info, Trash2, Plus, CalendarClock, Inbox, Zap, ArrowUp, ArrowDown, Mail, MessageCircle, MessageSquare } from "lucide-react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { WorkflowCondition, WorkflowDelay, AgentSummary, AgentOutfield, AgentInfield } from "../../api";
import { AppNode, AppEdge } from "../../store";
import { cn } from "../../../../lib/utils";
import { TemplateFieldInput } from "../TemplateFieldInput";
import { Dropdown } from "../../../../components/ui/dropdown";
import { ToggleSwitch } from "../../../../components/ui/toggle-switch";
import { useAuth } from "../../../../context/AuthContext";
import { whatsappApi } from "../../../admin/whatsapp-api";
import { chatbotApi } from "../../../chatbot/chatbot-api";
import { templatesApi } from "../../../whatsapp-templates/api";

interface InspectorPanelProps {
  selectedNode: AppNode | null;
  selectedEdge: AppEdge | null;
  agents: AgentSummary[];
  outfieldsMap: Record<string, AgentOutfield[]>;
  infieldsMap?: Record<string, AgentInfield[]>;
  sourceAgentId: string | null;
  /** Agent id(s) of the nearest call step(s) upstream of the selected node —
   *  the agents whose in/outfields an action step may reference. */
  upstreamAgentIds?: string[];
  /** 1-based position of this edge among its source node's branches. */
  branchOrder?: number;
  /** How many branches leave this edge's source node. */
  branchCount?: number;
  /** Reorder this branch by `delta` places (-1 earlier, +1 later). */
  onMoveEdge?: (delta: number) => void;
  workflow?: any; // kept for compatibility, though we use Zustand now
  onUpdateWorkflow?: (patch: any) => void;
  onClose: () => void;
  onUpdateNode: (patch: any) => void;
  onUpdateEdge: (patch: any) => void;
  onDeleteEdge?: () => void;
}

const OPERATOR_LABELS: Record<string, string> = {
  equals: "equals",
  not_equals: "not equals",
  in: "contains",
  is_set: "is set",
  is_not_set: "is not set",
};

const VALUELESS_OPERATORS = new Set(["is_set", "is_not_set"]);

export function InspectorPanel({
  selectedNode,
  selectedEdge,
  agents,
  outfieldsMap,
  infieldsMap = {},
  sourceAgentId,
  upstreamAgentIds = [],
  branchOrder = 0,
  branchCount = 0,
  onMoveEdge,
  workflow,
  onUpdateWorkflow,
  onClose,
  onUpdateNode,
  onUpdateEdge,
  onDeleteEdge
}: InspectorPanelProps) {

  const actionType =
    selectedNode?.type === "email" || selectedNode?.type === "whatsapp" || selectedNode?.type === "sms"
      ? selectedNode.type
      : null;
  const isEmailLevel = actionType === "email";
  const isMessageLevel = actionType === "whatsapp" || actionType === "sms";
  const isActionLevel = !!actionType;

  const isWorkflowLevel = !selectedNode && !selectedEdge;
  // An action step (email / whatsapp / sms) has no agent/outfield section, so
  // it gets its own mode rather than sharing the call-node one.
  const isNodeLevel = !!selectedNode && !isActionLevel;
  const isEdgeLevel = !!selectedEdge;

  // Local state for Workflow Level inputs to avoid race conditions with optimistic updates
  const [localWorkflow, setLocalWorkflow] = useState({
    name: workflow?.name || "",
    max_steps: workflow?.max_steps || 10,
    description: workflow?.description || "",
    whatsapp_instance_name: workflow?.whatsapp_instance_name || "",
  });
  
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (workflow) {
      setLocalWorkflow(prev => ({
        // Only override if we are not actively typing (wait, the debounce timeout tracks this roughly)
        name: updateTimeoutRef.current ? prev.name : (workflow.name || ""),
        max_steps: updateTimeoutRef.current ? prev.max_steps : (workflow.max_steps || 10),
        description: updateTimeoutRef.current ? prev.description : (workflow.description || ""),
        whatsapp_instance_name: updateTimeoutRef.current ? prev.whatsapp_instance_name : (workflow.whatsapp_instance_name || ""),
      }));
    }
  }, [workflow]);

  const handleUpdateWorkflow = (patch: any) => {
    setLocalWorkflow(prev => ({ ...prev, ...patch }));
    
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }
    
    updateTimeoutRef.current = setTimeout(() => {
      onUpdateWorkflow?.(patch);
      updateTimeoutRef.current = null;
    }, 800);
  };

  // Node data
  const nodeData = selectedNode?.data as any;
  const nodeLabel = nodeData?.nodeLabel || "";
  const agentName = nodeData?.agentName || "";

  // Edge data
  const edgeData = selectedEdge?.data as any;
  const conditions = (edgeData?.conditions as WorkflowCondition[]) || [];
  const delay = (edgeData?.delay as WorkflowDelay) || null;
  const priority = edgeData?.priority ?? 0;
  // The operator enabled "Add delay" but never typed a duration — blocks save.
  const delayIncomplete = !!delay && !(Number(delay.value) >= 1);

  const updateCondition = (index: number, patch: Partial<WorkflowCondition>) => {
    const updated = [...conditions];
    updated[index] = { ...updated[index], ...patch };
    onUpdateEdge({ conditions: updated });
  };

  const addCondition = () => {
    onUpdateEdge({
      conditions: [...conditions, { field: "", operator: "equals", value: "" }]
    });
  };

  const removeCondition = (index: number) => {
    onUpdateEdge({ conditions: conditions.filter((_, i) => i !== index) });
  };

  const toggleDelay = () => {
    if (delay) {
      onUpdateEdge({ delay: null });
    } else {
      onUpdateEdge({ delay: { value: 2, unit: "days" } });
    }
  };

  // ── Action-step config (email / whatsapp / sms). Edits go through
  // onUpdateNode like any other node field, so they ride the normal
  // Save/dirty flow.
  const emailTemplate = (nodeData?.email as any) || {};
  const updateEmail = (patch: Record<string, any>) =>
    onUpdateNode({ email: { ...emailTemplate, ...patch } });

  const messageConfig = (actionType ? (nodeData?.[actionType] as any) : null) || {};
  const updateMessage = (patch: Record<string, any>) =>
    actionType && onUpdateNode({ [actionType]: { ...messageConfig, ...patch } });

  // Workflow follow-ups use a number explicitly enabled for that purpose.
  // The action then records which configured Chatbot agent should answer replies.
  const { token, user } = useAuth() as any;
  const clientId = user?.client_id || user?.id || "";
  const whatsappConnections = useQuery({
    queryKey: ["whatsapp-connections", token],
    queryFn: () => whatsappApi.listForClient(token, clientId),
    enabled: Boolean(token) && actionType === "whatsapp",
  });
  const chatbotAgents = useQuery({
    queryKey: ["chatbot-agents", token],
    queryFn: () => chatbotApi.agents(token),
    enabled: Boolean(token) && actionType === "whatsapp",
  });
  const whatsappTemplates = useQuery({
    queryKey: ["whatsapp-templates", token],
    queryFn: () => templatesApi.list(),
    enabled: Boolean(token) && actionType === "whatsapp",
  });
  const workflowInstance = whatsappConnections.data?.find(
    instance => instance.purposes?.includes("workflow")
  );
  const instanceOptions = whatsappConnections.data ?? [];
  // Per-step sender override. Empty = workflow default (the workflow-purpose
  // instance above). Stored verbatim on the step for the engine to honor.
  const selectedInstanceName = String(messageConfig.whatsapp_instance_name || workflowInstance?.instance_name || "");
  const effectiveInstance = instanceOptions.find(i => i.instance_name === selectedInstanceName) ?? workflowInstance;
  const instanceLabel = (instance: any) =>
    instance.phone_number ? `+${instance.phone_number}` : instance.instance_name;
  const availableChatbotAgents = (chatbotAgents.data ?? []).filter(
    agent => agent.system_prompt?.trim()
  );

  /** In/outfields of the call step(s) that feed this action step — the only
   *  tokens a template/message can actually resolve at send time. When the
   *  step isn't wired to an agent yet, `upstreamAgentIds` is empty and the
   *  token list is hidden with a hint to connect it. */
  const uniqSorted = (names: string[]) =>
    Array.from(new Set(names.map(n => String(n || "").trim()).filter(Boolean))).sort();
  const outfieldTokens = uniqSorted(
    upstreamAgentIds.flatMap(aid => outfieldsMap[aid] || []).map(f => f?.fieldName || "")
  );
  const infieldTokens = uniqSorted(
    upstreamAgentIds.flatMap(aid => infieldsMap[aid] || []).map(f => f?.key || "")
  );
  // Union — used by the recipient picker and the "not a field" fallback check.
  const availableTokens = uniqSorted([...outfieldTokens, ...infieldTokens]);
  const hasUpstreamAgent = upstreamAgentIds.length > 0;

  // Whatsapp template mode: saved library row + per-{{n}} field mapping.
  // Only Meta-approved templates are attachable: a Cloud-API number refuses
  // the rest outside the 24-hour window. A previously attached template that
  // has since lapsed stays visible, marked, so the author can see what broke.
  const isWhatsappTemplateMode = actionType === "whatsapp" && String(messageConfig.message_kind || "free_text") === "template";
  const templateList = (whatsappTemplates.data as any[]) || [];
  const approvedTemplateList = templateList.filter(
    (t: any) => String(t.status || "draft").toLowerCase() === "approved",
  );
  const selectedTemplate = templateList.find(
    (t: any) => String(t.template_id) === String(messageConfig.template_id || "") || String(t.name) === String(messageConfig.template_id || ""),
  );
  const templatePreviewBody = String(
    selectedTemplate?.body || (messageConfig.template_snapshot as any)?.body || "",
  );
  const templateVariables: number[] = (() => {
    const stored = selectedTemplate?.variables || (messageConfig.template_snapshot as any)?.variables;
    if (Array.isArray(stored) && stored.length) {
      return [...new Set(stored.map((v: any) => Number(v)).filter((n: number) => Number.isFinite(n)))].sort((a, b) => a - b);
    }
    const found = new Set<number>();
    for (const m of templatePreviewBody.matchAll(/\{\{(\d+)\}\}/g)) found.add(Number(m[1]));
    return [...found].sort((a, b) => a - b);
  })();
  const templateMapping = ((messageConfig.template_mapping as Record<string, string>) || {});
  const updateTemplateMapping = (num: number, field: string) =>
    updateMessage({ template_mapping: { ...templateMapping, [String(num)]: field } });

  const messageHeading = actionType === "sms" ? "SMS" : "WhatsApp";
  const MessageIcon = actionType === "sms" ? MessageSquare : MessageCircle;
  // Real channel colours — WhatsApp green, SMS blue, Email red.
  const actionBadgeClass =
    actionType === "email" ? "bg-[#EA4335]/10 text-[#C5221F]"
    : actionType === "sms" ? "bg-[#2563EB]/10 text-[#1D4ED8]"
    : "bg-[#25D366]/15 text-[#128C7E]";

  return (
    <aside className="absolute right-0 top-0 bottom-0 z-50 flex w-[380px] max-w-[45%] flex-col overflow-y-auto border-l border-line bg-elevated shadow-[-8px_0_32px_rgba(0,0,0,0.08)]">
      {isActionLevel && selectedNode ? (
        <div className="flex items-center justify-between border-b border-line bg-soft px-[18px] py-[14px] shrink-0">
          <div className="flex items-center gap-3">
            <div className={cn("flex h-8 w-8 items-center justify-center rounded-lg", actionBadgeClass)}>
              {isEmailLevel ? <Mail className="h-4 w-4" /> : <MessageIcon className="h-4 w-4" />}
            </div>
            <div className="flex flex-col">
              <span className="text-[14.5px] font-bold text-ink">
                {isEmailLevel ? "Email Step" : `${messageHeading} Step`}
              </span>
              <span className="font-mono text-[11.5px] text-ink-3">{selectedNode.id}</span>
            </div>
          </div>
          <button onClick={onClose} className="rounded p-1 text-ink-3 hover:bg-line hover:text-ink transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : isNodeLevel && selectedNode ? (
        <div className="flex items-center justify-between border-b border-line bg-soft px-[18px] py-[14px] shrink-0">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-soft text-[13px] font-bold text-brand">AI</div>
            <div className="flex flex-col">
              <span className="text-[14.5px] font-bold text-ink">{nodeLabel || "New Step"}</span>
              <span className="font-mono text-[11.5px] text-ink-3">{selectedNode.id}</span>
            </div>
          </div>
          <button onClick={onClose} className="rounded p-1 text-ink-3 hover:bg-line hover:text-ink transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-between border-b border-line bg-elevated px-[18px] py-[14px] shrink-0">
          <div className="flex items-center gap-3">
            {isEdgeLevel ? (
              <>
                <Network className="h-5 w-5 text-brand" />
                <h2 className="text-[14.5px] font-bold text-ink">Edit Branch Condition Outfield</h2>
              </>
            ) : (
              <>
                <Settings2 className="h-5 w-5 text-brand" />
                <h2 className="text-[14.5px] font-bold text-ink">Workflow Settings</h2>
              </>
            )}
          </div>
          <button onClick={onClose} className="rounded p-1 text-ink-3 hover:bg-soft hover:text-ink transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-5">

        {/* MODE 0a: Email step template */}
        {isEmailLevel && selectedNode && (
          <div className="flex flex-col gap-4">
            <p className="rounded-md border border-line bg-soft px-3 py-2 text-[11px] leading-relaxed text-ink-3">
              Sent when the branch leading into this step matches. Use{" "}
              <code className="font-mono text-ink-2">{"{{infield}}"}</code> anywhere below to
              drop in a value from the call. Type @ to choose a field.
            </p>

            <div className="flex flex-col gap-1.5">
              <label className="text-[12px] font-semibold text-ink-2">Step Name</label>
              <input
                type="text"
                value={String(nodeData?.nodeLabel || "")}
                onChange={(e) => onUpdateNode({ nodeLabel: e.target.value })}
                placeholder="Send Email"
                className="w-full rounded-[6px] border border-line bg-elevated px-2.5 py-2 text-[12.5px] text-ink outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition-shadow"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-[12px] font-semibold text-ink-2">To *</label>
              <TemplateFieldInput key={selectedNode.id + "-to"} tokens={infieldTokens} label="Email to"
                value={String(emailTemplate.to || "")}
                onValueChange={(value) => updateEmail({ to: value })}
                placeholder="{{lead_email}} or someone@company.com"
                className="w-full rounded-[6px] border border-line bg-elevated px-2.5 py-2 text-[12.5px] text-ink outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition-shadow"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-[12px] font-semibold text-ink-2">Cc</label>
              <input
                type="text"
                value={(emailTemplate.cc || []).join(", ")}
                onChange={(e) =>
                  updateEmail({ cc: e.target.value.split(",").map(v => v.trim()).filter(Boolean) })
                }
                placeholder="ops@company.com, sales@company.com"
                className="w-full rounded-[6px] border border-line bg-elevated px-2.5 py-2 text-[12.5px] text-ink outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition-shadow"
              />
              <span className="text-[11px] text-ink-3">Comma separated. Optional.</span>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-[12px] font-semibold text-ink-2">Subject *</label>
              <TemplateFieldInput key={selectedNode.id + "-subject"} tokens={infieldTokens} label="Email subject"
                value={String(emailTemplate.subject || "")}
                onValueChange={(value) => updateEmail({ subject: value })}
                placeholder="Thanks for your time, {{lead_name}}"
                className="w-full rounded-[6px] border border-line bg-elevated px-2.5 py-2 text-[12.5px] text-ink outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition-shadow"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-[12px] font-semibold text-ink-2">Body *</label>
              <TemplateFieldInput key={selectedNode.id + "-body"} tokens={infieldTokens} label="Email body"
                rows={8}
                value={String(emailTemplate.body || "")}
                onValueChange={(value) => updateEmail({ body: value })}
                placeholder={"<p>Hi {{lead_name}},</p>\n<p>Thanks for speaking with us today.</p>"}
                className="w-full resize-y rounded-[6px] border border-line bg-elevated px-2.5 py-2 font-mono text-[12px] text-ink outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition-shadow"
              />
              <span className="text-[11px] text-ink-3">
                Basic HTML is supported. A plain-text version is generated automatically.
              </span>
            </div>

            {infieldTokens.length > 0 && (
              <div className="flex flex-col gap-2 border-t border-line pt-3">
                <span className="text-[12px] font-semibold text-ink-2">
                  Available fields <span className="font-normal text-ink-3">(from the connected call step)</span>
                </span>
                {infieldTokens.length > 0 && (
                  <div className="flex flex-col gap-1">
                    <span className="text-[10.5px] font-semibold uppercase tracking-wider text-ink-3">Infields (call inputs)</span>
                    <div className="flex flex-wrap gap-1.5">
                      {infieldTokens.map(token => (
                        <button
                          key={token}
                          type="button"
                          onClick={() => updateEmail({ body: `${String(emailTemplate.body || "")}{{${token}}}` })}
                          title={`Append {{${token}}} to the body`}
                          className="rounded border border-line bg-soft px-1.5 py-0.5 font-mono text-[10.5px] text-ink-2 hover:border-brand hover:text-brand transition-colors"
                        >
                          {`{{${token}}}`}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <span className="text-[11px] text-ink-3">
                  A token with no value on the call cancels that email rather than sending it
                  half-filled.
                </span>
              </div>
            )}

            {!hasUpstreamAgent && (
              <p className="border-t border-line pt-3 text-[11px] text-warning">
                Connect this step to a call step to use that agent's infields as
                <code className="mx-1 font-mono">{"{{tokens}}"}</code>.
              </p>
            )}
          </div>
        )}

        {/* MODE 0b: WhatsApp / SMS step message */}
        {isMessageLevel && selectedNode && actionType && (
          <div className="flex flex-col gap-4">
            <p className="rounded-md border border-line bg-soft px-3 py-2 text-[11px] leading-relaxed text-ink-3">
              Sent when the branch leading into this step matches. Use{" "}
              <code className="font-mono text-ink-2">{"{{infield}}"}</code> in the message to drop
              in a value from the call. Type @ to choose a field.
              {actionType === "whatsapp" && " The WhatsApp instance is set in Workflow Settings."}
            </p>

            <div className="flex flex-col gap-1.5">
              <label className="text-[12px] font-semibold text-ink-2">Step Name</label>
              <input
                type="text"
                value={String(nodeData?.nodeLabel || "")}
                onChange={(e) => onUpdateNode({ nodeLabel: e.target.value })}
                placeholder={`Send ${messageHeading}`}
                className="w-full rounded-[6px] border border-line bg-elevated px-2.5 py-2 text-[12.5px] text-ink outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition-shadow"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-[12px] font-semibold text-ink-2">Recipient</label>
              <select
                value={messageConfig.recipient_source === "outfield" ? "outfield" : "customer_phone"}
                onChange={(e) => updateMessage({ recipient_source: e.target.value })}
                className="w-full rounded-[6px] border border-line bg-elevated px-2.5 py-2 text-[12.5px] text-ink outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition-shadow"
              >
                <option value="customer_phone">The customer's own number</option>
                <option value="outfield">A number from an outfield</option>
              </select>
            </div>

            {messageConfig.recipient_source === "outfield" && (
              <div className="flex flex-col gap-1.5">
                <label className="text-[12px] font-semibold text-ink-2">Recipient field *</label>
                <select
                  value={String(messageConfig.recipient_field || "")}
                  onChange={(e) => updateMessage({ recipient_field: e.target.value })}
                  className="w-full rounded-[6px] border border-line bg-elevated px-2.5 py-2 text-[12.5px] text-ink outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition-shadow"
                >
                  <option value="" disabled>Select outfield...</option>
                  {availableTokens.map(token => (
                    <option key={token} value={token}>{token}</option>
                  ))}
                  {!availableTokens.includes(String(messageConfig.recipient_field || "")) &&
                    messageConfig.recipient_field && (
                      <option value={messageConfig.recipient_field}>
                        {messageConfig.recipient_field} (not an outfield)
                      </option>
                    )}
                </select>
                {!hasUpstreamAgent && (
                  <span className="text-[11px] text-warning">
                    Connect this step to a call step first — its agent's outfields fill this list.
                  </span>
                )}
              </div>
            )}

            {actionType === "whatsapp" && (
              <div className="flex flex-col gap-1.5">
                <label className="text-[12px] font-semibold text-ink-2">Message type</label>
                <select
                  value={isWhatsappTemplateMode ? "template" : "free_text"}
                  onChange={(e) => updateMessage({
                    message_kind: e.target.value,
                    ...(e.target.value === "template" ? {} : {}),
                  })}
                  className="w-full rounded-[6px] border border-line bg-elevated px-2.5 py-2 text-[12.5px] text-ink outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition-shadow"
                >
                  <option value="free_text">Free text message</option>
                  <option value="template">Saved template</option>
                </select>
                {isWhatsappTemplateMode && (
                  <span className="text-[11px] leading-relaxed text-ink-3">
                    Renders a saved template with call values (same as campaigns). Missing values cancel that send.
                  </span>
                )}
              </div>
            )}

            {isWhatsappTemplateMode ? (
              <>
                <div className="flex flex-col gap-1.5">
                  <label className="text-[12px] font-semibold text-ink-2">Template * (Meta-approved)</label>
                  <select
                    value={String(messageConfig.template_id || "")}
                    onChange={(e) => {
                      const picked = templateList.find((t: any) => String(t.template_id) === e.target.value || String(t.name) === e.target.value);
                      updateMessage({
                        template_id: e.target.value,
                        template_name: picked ? String(picked.name || "") : "",
                      });
                    }}
                    className="w-full rounded-[6px] border border-line bg-elevated px-2.5 py-2 text-[12.5px] text-ink outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition-shadow"
                  >
                    <option value="" disabled>
                      {whatsappTemplates.isLoading ? "Loading templates..." : approvedTemplateList.length === 0 ? "No approved template yet…" : "Select template..."}
                    </option>
                    {approvedTemplateList.map((t: any) => (
                      <option key={t.template_id} value={t.template_id}>
                        {t.name}
                      </option>
                    ))}
                    {messageConfig.template_id && !selectedTemplate && (
                      <option value={messageConfig.template_id}>
                        {messageConfig.template_name || messageConfig.template_id} (missing)
                      </option>
                    )}
                    {selectedTemplate && String(selectedTemplate.status || "draft").toLowerCase() !== "approved" && (
                      <option value={selectedTemplate.template_id}>
                        {selectedTemplate.name} ({String(selectedTemplate.status || "draft").toUpperCase()} — re-approve to send)
                      </option>
                    )}
                  </select>
                  {approvedTemplateList.length === 0 && !whatsappTemplates.isLoading && (
                    <span className="text-[11px] text-warning">
                      No approved template yet — submit one under WhatsApp → Template first.
                    </span>
                  )}
                  {!hasUpstreamAgent && (
                    <span className="text-[11px] text-warning">
                      Connect this step to a call step first — its agent's outfields fill the variable list.
                    </span>
                  )}
                </div>

                {selectedTemplate || templatePreviewBody ? (
                  <div className="rounded-md border border-line bg-soft px-3 py-2 text-[11.5px] leading-relaxed text-ink-2">
                    {templatePreviewBody || "No preview."}
                  </div>
                ) : null}
                {selectedTemplate && String(selectedTemplate.status || "draft").toLowerCase() !== "approved" && (
                  <span className="text-[11px] text-warning">
                    {selectedTemplate.name} is {String(selectedTemplate.status || "draft").toUpperCase()} on Meta —
                    Cloud-API numbers outside the 24-hour window will refuse it. Approve it under WhatsApp → Template.
                  </span>
                )}

                {templateVariables.length > 0 && (
                  <div className="flex flex-col gap-2">
                    <span className="text-[12px] font-semibold text-ink-2">
                      Variables <span className="font-normal text-ink-3">(map each to a call field)</span>
                    </span>
                    {templateVariables.map(num => (
                      <label key={num} className="flex items-center gap-2 text-[12px] text-ink">
                        <code className="w-12 shrink-0 rounded border border-line bg-elevated px-1.5 py-1 font-mono text-[11px] text-ink-2">{`{{${num}}}`}</code>
                        <select
                          value={String(templateMapping[String(num)] || "")}
                          onChange={(e) => updateTemplateMapping(num, e.target.value)}
                          className="w-full rounded-[6px] border border-line bg-elevated px-2 py-1.5 text-[12px] text-ink outline-none focus:border-brand"
                        >
                          <option value="" disabled>Select field...</option>
                          {availableTokens.map(token => (
                            <option key={token} value={token}>{token}</option>
                          ))}
                        </select>
                      </label>
                    ))}
                  </div>
                )}
              </>
            ) : (
            <div className="flex flex-col gap-1.5">
              <label className="text-[12px] font-semibold text-ink-2">Message *</label>
              <TemplateFieldInput key={selectedNode.id + "-message"} tokens={infieldTokens} label={messageHeading + " message"}
                rows={6}
                value={String(messageConfig.message_body || "")}
                onValueChange={(value) => updateMessage({ message_body: value })}
                placeholder={"Hi {{lead_name}}, thanks for your time today."}
                className="w-full resize-y rounded-[6px] border border-line bg-elevated px-2.5 py-2 text-[12.5px] text-ink outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition-shadow"
              />
            </div>
            )}

            {actionType === "whatsapp" && (
              <div className="flex flex-col gap-1.5 rounded-md border border-line bg-soft px-3 py-2.5">
                <label className="flex items-center justify-between gap-2 text-[12.5px] text-ink">
                  <span className="font-semibold text-ink-2">Attach WhatsApp AI agent</span>
                  <ToggleSwitch
                    ariaLabel="Attach WhatsApp AI agent"
                    checked={Boolean(messageConfig.ai_agent_handoff)}
                    disabled={!workflowInstance || availableChatbotAgents.length === 0}
                    onChange={(checked) => updateMessage({
                      ai_agent_handoff: checked,
                      ai_agent_id: checked ? String(messageConfig.ai_agent_id || availableChatbotAgents[0]?.id || "") : "",
                      whatsapp_instance_name: checked ? String(messageConfig.whatsapp_instance_name || workflowInstance?.instance_name || "") : "",
                    })}
                  />
                </label>
                {!workflowInstance ? (
                  <span className="text-[11px] leading-relaxed text-ink-3">
                    Turn on Workflow follow-ups for a number in{" "}
                    <Link className="font-semibold text-brand" to="/settings?section=whatsapp">Settings</Link> first.
                  </span>
                ) : availableChatbotAgents.length === 0 ? (
                  <span className="text-[11px] leading-relaxed text-ink-3">
                    Create an agent with a system prompt in <Link className="font-semibold text-brand" to="/chatbot">Chatbot</Link> first.
                  </span>
                ) : messageConfig.ai_agent_handoff ? (
                  <div className="mt-1 flex flex-col gap-2.5">
                    <label className="flex flex-col gap-1.5 text-[12px] text-ink">
                      <span className="font-semibold text-ink-2">WhatsApp number</span>
                      <Dropdown
                        ariaLabel="WhatsApp number"
                        value={selectedInstanceName}
                        disabled={whatsappConnections.isLoading || instanceOptions.length === 0}
                      options={instanceOptions.map(i => ({ value: i.instance_name, label: `${instanceLabel(i)}${i.status !== "connected" ? ` (${i.status})` : ""}` }))}
                        onChange={(v) => updateMessage({ whatsapp_instance_name: v })}
                        className="bg-elevated"
                      />
                    </label>
                    <div className="flex flex-col gap-1.5">
                      <span className="text-[11px] font-semibold text-ink-2">Chatbot agent</span>
                      <Dropdown
                        ariaLabel="WhatsApp AI agent"
                        value={String(messageConfig.ai_agent_id || "")}
                        options={availableChatbotAgents.map(agent => ({ value: agent.id, label: agent.name }))}
                        onChange={(ai_agent_id) => updateMessage({ ai_agent_id })}
                        className="bg-elevated"
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            )}

            {infieldTokens.length > 0 && (
              <div className="flex flex-col gap-2 border-t border-line pt-3">
                <span className="text-[12px] font-semibold text-ink-2">
                  Available fields <span className="font-normal text-ink-3">(from the connected call step)</span>
                </span>
                {infieldTokens.length > 0 && (
                  <div className="flex flex-col gap-1">
                    <span className="text-[10.5px] font-semibold uppercase tracking-wider text-ink-3">Infields (call inputs)</span>
                    <div className="flex flex-wrap gap-1.5">
                      {infieldTokens.map(token => (
                        <button
                          key={token}
                          type="button"
                          onClick={() =>
                            updateMessage({ message_body: `${String(messageConfig.message_body || "")}{{${token}}}` })
                          }
                          title={`Append {{${token}}} to the message`}
                          className="rounded border border-line bg-soft px-1.5 py-0.5 font-mono text-[10.5px] text-ink-2 hover:border-brand hover:text-brand transition-colors"
                        >
                          {`{{${token}}}`}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <span className="text-[11px] text-ink-3">
                  A token with no value on the call cancels that message rather than sending it
                  half-filled.
                </span>
              </div>
            )}

            {!hasUpstreamAgent && (
              <p className="border-t border-line pt-3 text-[11px] text-warning">
                Connect this step to a call step to use that agent's infields as
                <code className="mx-1 font-mono">{"{{tokens}}"}</code>.
              </p>
            )}
          </div>
        )}

        {/* MODE 1: Workflow Level */}
        {isWorkflowLevel && workflow && (
          <>
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold text-ink-2">Name *</label>
              <input
                type="text"
                className="rounded-md border border-line bg-elevated px-3 py-2 text-sm text-ink focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand shadow-sm transition-shadow"
                value={localWorkflow.name}
                onChange={(e) => handleUpdateWorkflow({ name: e.target.value })}
              />
            </div>
            
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold text-ink-2">Maximum Steps</label>
              <input
                type="number"
                min={1}
                className="rounded-md border border-line bg-elevated px-3 py-2 text-sm text-ink focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand shadow-sm transition-shadow"
                value={localWorkflow.max_steps}
                onChange={(e) => handleUpdateWorkflow({ max_steps: parseInt(e.target.value) || 1 })}
              />
              <p className="text-[11px] text-ink-3">Helper: Limit to prevent infinite loops</p>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold text-ink-2">Description</label>
              <textarea
                className="rounded-md border border-line bg-elevated px-3 py-2 text-sm text-ink focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand shadow-sm transition-shadow resize-none h-24"
                value={localWorkflow.description}
                onChange={(e) => handleUpdateWorkflow({ description: e.target.value })}
              />
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold text-ink-2">WhatsApp instance name</label>
              <input
                type="text"
                readOnly
                disabled
                className="rounded-md border border-line bg-surface px-3 py-2 text-sm text-ink-3 shadow-sm cursor-not-allowed"
                value={localWorkflow.whatsapp_instance_name || 'Set per client'}
              />
              <p className="text-[11px] text-ink-3">
                WhatsApp steps send from the instance configured for the client, in Admin → the
                client → WhatsApp. This field no longer routes anything and is kept only so older
                published versions still read back unchanged.
              </p>
            </div>
          </>
        )}

        {/* MODE 2: Node Level */}
        {isNodeLevel && selectedNode && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-[12px] font-semibold text-ink-2">Step Name</label>
              <input
                type="text"
                className="w-full rounded-[6px] border border-line bg-elevated px-2.5 py-2 text-[12.5px] text-ink outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition-shadow"
                value={nodeLabel}
                onChange={(e) => onUpdateNode({ nodeLabel: e.target.value })}
              />
            </div>

            {/* The start step is a real call too — it needs an agent like any
                other node, so the picker is shown for it as well. */}
            <div className="flex flex-col gap-1.5">
              <label className="text-[12px] font-semibold text-ink-2">
                {selectedNode.type === 'start' ? 'Agent (first call)' : 'Agent Assigned'}
              </label>
              <div className="flex flex-col gap-2 rounded-[8px] border border-line bg-soft p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    <Inbox className="h-4 w-4 text-ink-2 shrink-0" />
                    <Dropdown
                      className="min-w-0 flex-1 bg-elevated py-1.5 text-[12.5px] font-medium"
                      ariaLabel="Select agent"
                      value={agentName}
                      onChange={(value) => onUpdateNode({ agentName: value })}
                      options={agents.map(a => ({
                        value: a.agent_id,
                        label: a.name || a.agent_name || "Unnamed Agent",
                      }))}
                    />
                  </div>
                  {agentName && (
                    <span className="rounded bg-success-soft px-2 py-0.5 text-[10px] font-bold text-success border border-success/30 uppercase tracking-wide">
                      ACTIVE
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-ink-3">
                  {selectedNode.type === 'start'
                    ? "This agent places the first call of every run."
                    : "Primary voice agent handling this call milestone."}
                </p>
              </div>
            </div>

            {agentName && (
              <div className="flex flex-col gap-1.5 mt-1">
                <div className="flex items-center justify-between">
                  <label className="text-[12px] font-semibold text-ink-2">Outfields Extracted</label>
                  <span className="text-[11px] text-ink-3">{(outfieldsMap[agentName] || []).length} fields</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(outfieldsMap[agentName] || []).map(field => (
                    <span key={field.fieldName} className="inline-flex items-center gap-1 rounded bg-soft px-2 py-1 font-mono text-[11px] text-ink-2 border border-line whitespace-nowrap">
                      <Zap className="h-3 w-3 text-brand fill-brand" />
                      {field.fieldName}
                    </span>
                  ))}
                  {(!outfieldsMap[agentName] || outfieldsMap[agentName].length === 0) && (
                    <span className="text-[11px] text-warning italic">This agent has no outfields — branches from this step can't be conditioned.</span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* MODE 3: Edge Level */}
        {isEdgeLevel && selectedEdge && (
          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-1 text-[11px] text-ink-3">
              <div>From: <span className="font-mono text-ink-2">{selectedEdge.source}</span></div>
              <div>To: <span className="font-mono text-ink-2">{selectedEdge.target}</span></div>
            </div>

            <div className="flex flex-col gap-3">
              <div>
                <label className="text-[13px] font-bold text-ink">Condition Outfields</label>
                <p className="text-[11px] text-ink-3 mt-0.5">Edge fires when ALL condition outfields match</p>
              </div>

              {selectedEdge?.source !== "start" &&
                (!sourceAgentId || (outfieldsMap[sourceAgentId] || []).length === 0) && (
                  <div className="text-[11px] text-warning bg-warning-soft p-3 rounded-[8px] border border-warning/30 flex gap-2">
                    <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    <span>
                      The source step{sourceAgentId ? ` (agent ${sourceAgentId})` : ""} has no
                      outfields, so there is no field to branch on. Add outfields to that agent —
                      otherwise this edge cannot be published.
                    </span>
                  </div>
                )}

              {conditions.length === 0 && selectedEdge?.source !== "start" && (
                <div className="text-[11px] text-danger italic bg-danger-soft p-4 rounded-[8px] border border-danger/30 border-dashed text-center">
                  Error: An edge must have at least one condition outfield. There are no "default" edges.
                </div>
              )}
              {conditions.length === 0 && selectedEdge?.source === "start" && (
                <div className="text-[11px] text-ink-3 italic bg-soft p-4 rounded-[8px] border border-line border-dashed text-center">
                  Workflow trigger branch (Unconditional condition outfield)
                </div>
              )}

              {conditions.map((cond, idx) => {
                const isValueless = VALUELESS_OPERATORS.has(cond.operator);
                return (
                  <div key={idx} className="flex flex-col p-3.5 rounded-[8px] border border-line bg-soft relative gap-3.5 shadow-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-[12px] font-semibold text-ink-2">Condition Outfield {idx + 1}</span>
                      <button onClick={() => removeCondition(idx)} className="text-ink-3 hover:text-danger transition-colors">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>

                    <div className="grid grid-cols-[65px_1fr] items-center gap-2">
                      <label className="text-[12px] text-ink-2">Condition Outfield</label>
                      <select
                        className="w-full rounded-[6px] border border-line bg-elevated px-2.5 py-2 text-[12.5px] text-ink outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition-shadow"
                        value={cond.field}
                        onChange={(e) => updateCondition(idx, { field: e.target.value })}
                      >
                        <option value="" disabled>Select field...</option>
                        {(sourceAgentId && outfieldsMap[sourceAgentId] ? outfieldsMap[sourceAgentId] : []).map(of => (
                          <option key={of.fieldName} value={of.fieldName}>{of.fieldName}</option>
                        ))}
                        {(!sourceAgentId || !outfieldsMap[sourceAgentId]?.length) && (
                          cond.field
                            ? <option value={cond.field}>{cond.field} (not an outfield)</option>
                            : <option value="" disabled>No outfields on the source agent</option>
                        )}
                      </select>
                    </div>

                    <div className="grid grid-cols-[65px_1fr] items-center gap-2">
                      <label className="text-[12px] text-ink-2">Operator</label>
                      <select
                        className="w-full rounded-[6px] border border-line bg-elevated px-2.5 py-2 text-[12.5px] text-ink outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition-shadow"
                        value={cond.operator}
                        onChange={(e) => updateCondition(idx, { operator: e.target.value as any })}
                      >
                        {Object.entries(OPERATOR_LABELS).map(([val, label]) => (
                          <option key={val} value={val}>{label}</option>
                        ))}
                      </select>
                    </div>

                    {!isValueless && (
                      <div className="grid grid-cols-[65px_1fr] items-center gap-2">
                        <label className="text-[12px] text-ink-2">Value</label>
                        <input
                          type="text"
                          placeholder="e.g. yes"
                          className="w-full rounded-[6px] border border-line bg-elevated px-2.5 py-2 text-[12.5px] text-ink outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition-shadow"
                          value={cond.value || ""}
                          onChange={(e) => updateCondition(idx, { value: e.target.value })}
                        />
                      </div>
                    )}
                  </div>
                );
              })}

              <button 
                onClick={addCondition}
                className="mt-1 flex items-center justify-center gap-1.5 w-full py-2 rounded-[6px] bg-elevated text-[12.5px] font-semibold text-ink-2 hover:bg-soft transition-colors border border-transparent hover:border-line"
              >
                <Plus className="h-3.5 w-3.5" /> Add Condition Outfield
              </button>
            </div>

            <div className="flex flex-col gap-2 pt-5 border-t border-line">
              <div className="flex items-center justify-between">
                <label className="text-[12px] font-semibold text-ink-2">Priority</label>
                <span className="text-[11px] font-medium text-ink-3">
                  {branchOrder > 0 ? `Branch ${branchOrder} of ${branchCount}` : "—"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={0}
                  className="w-24 rounded-[6px] border border-line bg-elevated px-2.5 py-1.5 text-[12.5px] text-ink outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition-shadow"
                  value={priority}
                  onChange={(e) => {
                    const value = e.target.value;
                    onUpdateEdge({
                      priority: value === "" ? "" : parseInt(value, 10),
                    });
                  }}
                />
                <div className="flex flex-col">
                  <button
                    onClick={() => onMoveEdge?.(-1)}
                    disabled={branchOrder <= 1}
                    title="Check this branch earlier"
                    className="rounded-t-[6px] border border-b-0 border-line bg-elevated px-1.5 py-0.5 text-ink-3 hover:bg-soft hover:text-ink disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <ArrowUp className="h-3 w-3" />
                  </button>
                  <button
                    onClick={() => onMoveEdge?.(1)}
                    disabled={branchCount === 0 || branchOrder >= branchCount}
                    title="Check this branch later"
                    className="rounded-b-[6px] border border-line bg-elevated px-1.5 py-0.5 text-ink-3 hover:bg-soft hover:text-ink disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <ArrowDown className="h-3 w-3" />
                  </button>
                </div>
              </div>
              <p className="text-[11px] text-ink-3">
                Lower number is checked first. Type a priority manually, or use the arrows to move
                this branch earlier or later.
              </p>
            </div>

            <div className="flex flex-col gap-3 pt-4 border-t border-line">
              <label className="text-[12px] font-semibold text-ink-2">Delay Before Execution</label>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="enable-delay"
                  checked={!!delay}
                  onChange={toggleDelay}
                  className="rounded border-line text-brand focus:ring-brand h-4 w-4"
                />
                <label htmlFor="enable-delay" className="text-[13px] font-medium text-ink">Add delay</label>
              </div>

              {delay && (
                <div className={cn(
                  "flex gap-2 items-center p-2.5 rounded-[8px] border shadow-sm mt-1",
                  delayIncomplete ? "bg-danger-soft border-danger/40" : "bg-soft border-line"
                )}>
                  <CalendarClock className={cn("h-4 w-4 ml-1", delayIncomplete ? "text-danger" : "text-ink-3")} />
                  <input
                    type="number"
                    min={1}
                    placeholder="e.g. 2"
                    className={cn(
                      "w-16 rounded-[6px] border bg-elevated px-2 py-1.5 text-[12.5px] text-ink outline-none transition-shadow",
                      delayIncomplete
                        ? "border-danger/60 focus:border-danger focus:ring-2 focus:ring-danger/20"
                        : "border-line focus:border-brand focus:ring-2 focus:ring-brand/20"
                    )}
                    value={String(delay.value) === "" ? "" : delay.value}
                    onChange={(e) => {
                      const val = e.target.value;
                      onUpdateEdge({ delay: { ...delay, value: val === "" ? "" as any : parseInt(val) } });
                    }}
                  />
                  <select
                    className="flex-1 rounded-[6px] border border-line bg-elevated px-2.5 py-1.5 text-[12.5px] text-ink outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition-shadow"
                    value={delay.unit}
                    onChange={(e) => onUpdateEdge({ delay: { ...delay, unit: e.target.value as any } })}
                  >
                    <option value="minutes">minutes</option>
                    <option value="hours">hours</option>
                    <option value="days">days</option>
                  </select>
                </div>
              )}
              {delayIncomplete ? (
                <p className="text-[11px] font-semibold text-danger">
                  Enter how long to wait, or turn the delay off — the workflow can't be saved or
                  published with an empty delay.
                </p>
              ) : !delay && selectedEdge?.source !== "start" ? (
                <p className="text-[11px] font-semibold text-warning">
                  A delay is required on every branch before this workflow can be published.
                </p>
              ) : (
                <p className="text-[11px] text-ink-3">Helper: Wait period before the target call executes.</p>
              )}
            </div>

            <div className="pt-6 border-t border-line mt-2 pb-8">
              <button 
                onClick={onDeleteEdge} 
                className="flex items-center justify-center gap-2 w-full py-2.5 rounded-[6px] bg-danger-soft text-danger text-[12.5px] font-bold hover:bg-danger-soft transition-colors"
              >
                <Trash2 className="h-4 w-4" /> Delete Edge
              </button>
            </div>
          </div>
        )}

      </div>
    </aside>
  );
}
